import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  GetSerieDetailResponse,
  GetSerieEconomicsResponse,
  TermEconomicsItem,
} from '@/api/generated/schemas';
import {
  getGetVenuaPlannerSerieDetailQueryKey,
  useGetVenuaPlannerSerieDetail,
  useGetVenuaPlannerSerieEconomics,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { cn } from '@/shared/lib/utils';
import { Alert, Button, FormActionBar, Panel, Tabs, toast, type TabItem } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { plannerQueryKeys } from '../../plannerApi';
import { SerieBasicsForm } from './components/SerieBasicsForm';
import { serieDetailAgenda } from './serieDetailAgenda';
import { SerieLayerPipeline } from './components/SerieLayerPipeline';
import { SerieLockedPane } from './components/SerieLockedPane';
import { SerieStatsZone } from './components/SerieStatsZone';
import { SerieTermsTable } from './components/SerieTermsTable';
import { GenerateTermsDialog } from './generateTerms/GenerateTermsDialog';
import { formatDateRange } from './serieFormat';
import { SERIE_TAB_KEYS, toSerieStateKey, type SerieTabKey } from '../internal/serieLayers';
import { useSerieDetailForm } from './useSerieDetailForm';

interface SerieDetailPageProps {
  layerId: string;
}

/**
 * Thin loader: vyřeší parametry agendy a načte detail + ekonomiku seriálu. Vlastní obrazovku (včetně
 * editačního formuláře nad TanStack Form) vykreslí až s načteným detailem, aby `useForm` dostal správné
 * výchozí hodnoty hned (žádný `form.reset`).
 */
export function SerieDetailPage({ layerId }: SerieDetailPageProps) {
  const { t } = useTranslation('planner');
  const { open } = useAgendaParams(serieDetailAgenda, layerId);
  const { productId, serieId } = open;

  const detailQuery = useGetVenuaPlannerSerieDetail(productId, serieId);
  const economicsQuery = useGetVenuaPlannerSerieEconomics(productId, serieId);

  if (detailQuery.isLoading) {
    return (
      <div className="bg-background flex h-full items-center justify-center">
        <p className="text-phx-text-muted text-phx-sm">{t('serieDetail.loading')}</p>
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="bg-background h-full p-4">
        <Alert severity="error" title={t('serieDetail.loadErrorTitle')}>
          {t('serieDetail.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <SerieDetailView
      layerId={layerId}
      productId={productId}
      serieId={serieId}
      detail={detailQuery.data}
      economics={economicsQuery.data}
      isEconomicsLoading={economicsQuery.isLoading}
    />
  );
}

interface SerieDetailViewProps {
  layerId: string;
  productId: string;
  serieId: string;
  detail: GetSerieDetailResponse;
  economics: GetSerieEconomicsResponse | undefined;
  isEconomicsLoading: boolean;
}

/**
 * Detail termínového seriálu. Okamžitá data (metadata, stavy vrstev, termíny) přicházejí z `GetSerieDetail`,
 * ekonomika se dotahuje samostatně z `GetSerieEconomics` (do té doby skeletony). Údaje o seriálu se editují
 * přes TanStack Form a ukládají přes `UpdateSerie`.
 */
function SerieDetailView({
  layerId,
  productId,
  serieId,
  detail,
  economics,
  isEconomicsLoading,
}: SerieDetailViewProps) {
  const { t } = useTranslation('planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<SerieTabKey>('basics');

  const closeDetail = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const [generateTermsOpen, setGenerateTermsOpen] = useState(false);

  const notImplemented = useCallback(() => {
    toast({
      severity: 'info',
      title: t('messages.comingSoonTitle'),
      description: t('messages.notImplemented'),
    });
  }, [t]);

  const { form, submit, isSubmitting } = useSerieDetailForm({
    productId,
    serieId,
    detail,
    onSaved: () => {
      void queryClient.invalidateQueries({
        queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
      });
      void queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('serieDetail.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('serieDetail.toast.errorTitle'),
        description: t('serieDetail.toast.saveError'),
      });
    },
  });

  const economicsByTerm = useMemo(() => {
    const map = new Map<string, TermEconomicsItem>();
    economics?.terms.forEach((term) => map.set(term.termGroupId, term));
    return map;
  }, [economics]);

  const rangeLabel = formatDateRange(detail.dateRangeFrom, detail.dateRangeTo);

  const tabItems: TabItem<SerieTabKey>[] = SERIE_TAB_KEYS.map((tabKey, index) => {
    const layer = detail.layers.find((entry) => entry.sortOrder === index);
    const locked = layer ? toSerieStateKey(layer.state) === 'locked' : false;
    return {
      value: tabKey,
      label: (
        <span className={cn('gap-phx-2 inline-flex items-center', locked && 'opacity-60')}>
          {t(`serieDetail.tabs.${tabKey}`)}
          {locked ? <Lock size={11} className="text-phx-text-faint" /> : null}
        </span>
      ),
    };
  });

  return (
    <div className="bg-background flex h-full min-h-0 flex-col" data-testid="serie-detail">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="gap-phx-3 mx-auto flex w-full max-w-[1100px] flex-col p-4">
          <div className="flex flex-col">
            <SerieStatsZone
              detail={detail}
              economics={economics}
              isEconomicsLoading={isEconomicsLoading}
            />
            <SerieLayerPipeline layers={detail.layers} />
          </div>

          <Tabs<SerieTabKey>
            variant="underline"
            value={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />

          {activeTab === 'basics' ? (
            <form
              className="gap-phx-3 flex flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                void form.handleSubmit();
              }}
            >
              <Panel variant="filled" title={t('serieDetail.basics.panelTitle')}>
                <SerieBasicsForm form={form} dateRange={rangeLabel} />
              </Panel>

              <Panel
                variant="filled"
                icon="calendar"
                title={t('serieDetail.terms.panelTitle')}
                subtitle={t('serieDetail.terms.subtitle', {
                  count: detail.termsCount,
                  range: rangeLabel,
                })}
                padded={false}
                actions={
                  <div className="gap-phx-3 flex items-center">
                    {isEconomicsLoading ? (
                      <span className="text-phx-text-muted gap-phx-2 text-phx-sm inline-flex items-center whitespace-nowrap">
                        <span className="bg-phx-primary h-[7px] w-[7px] animate-pulse rounded-full" />
                        {t('serieDetail.terms.loadNote')}
                      </span>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      iconLeft="plus"
                      onClick={() => setGenerateTermsOpen(true)}
                    >
                      {t('serieDetail.terms.addTerms')}
                    </Button>
                  </div>
                }
                footer={
                  <div className="flex w-full items-center">
                    <span className="text-phx-text-muted">
                      {t('serieDetail.terms.footerCount', {
                        shown: detail.termsCount,
                        total: detail.termsCount,
                      })}
                    </span>
                    <Button
                      type="button"
                      className="ml-auto"
                      size="sm"
                      iconLeft="plus"
                      onClick={() => setGenerateTermsOpen(true)}
                    >
                      {t('serieDetail.terms.addTerms')}
                    </Button>
                  </div>
                }
              >
                <SerieTermsTable
                  terms={detail.terms}
                  economicsByTerm={economicsByTerm}
                  currency={economics?.currency ?? ''}
                  isEconomicsLoading={isEconomicsLoading}
                  onTermAction={notImplemented}
                />
              </Panel>
            </form>
          ) : (
            <SerieLockedPane title={t(`serieDetail.tabs.${activeTab}`)} />
          )}
        </div>
      </div>

      <FormActionBar onBack={closeDetail} onSave={submit} saving={isSubmitting} />

      {generateTermsOpen ? (
        <GenerateTermsDialog
          productId={productId}
          serieId={serieId}
          onClose={() => setGenerateTermsOpen(false)}
        />
      ) : null}
    </div>
  );
}
